import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScopeContract } from '../../contracts/scope';
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, TextField } from '@mui/material';

interface CreateApplicationFormProps {
    scopes: ScopeContract[];
    environment: string;
    onCreate: (name: string, scopeIds: string[]) => void;
    applicationLimit: number;
    currentCount: number;
}

const CreateApplicationForm: React.FC<CreateApplicationFormProps> = ({
    scopes,
    environment,
    onCreate,
    applicationLimit,
    currentCount,
}) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState<{ name?: string; scopes?: string }>({});
    const [isCreating, setIsCreating] = useState(false);
    const [scopeDropdownOpen, setScopeDropdownOpen] = useState(false);
    const autocompleteRef = useRef<any>(null);

    const canCreateApplication = currentCount < applicationLimit;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: { name?: string; scopes?: string } = {};

        if (!name.trim()) {
            newErrors.name = 'validation.applicationNameRequired';
        }

        if (selectedScopes.length === 0) {
            newErrors.scopes = 'validation.scopeRequired';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setIsCreating(true);
        try {
            await onCreate(name.trim(), selectedScopes);
            handleReset();
        } catch (error) {
            // Error is handled in parent component
        } finally {
            setIsCreating(false);
        }
    };

    const handleReset = () => {
        setName('');
        setSelectedScopes([]);
        setErrors({});
        setShowForm(false);
    };

    if (!canCreateApplication) {
        return (
            <div className="create-application-limit">
                <p>{t('applications.limitReached')}</p>
            </div>
        );
    }

    return (
        <div className="create-application-container">
            {isCreating && (
                <div className="fullpage-loader-overlay">
                    <div className="fullpage-loader">
                        <div className="loader-spinner"></div>
                    </div>
                </div>
            )}
            <div className="create-application-form-card">
                <div className="create-application-icon">
                    <AddIcon style={{ fontSize: '3rem', color: '#84cc16' }} />
                </div>
                <h3 className="create-application-form-title">{t('applications.newApplication')}</h3>
                <form onSubmit={handleSubmit} className="create-application-form">
                        <div className="create-application-field">
                            <label className="create-application-label">
                                {t('applications.name')} <span className="required">{t('applications.required')}</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setErrors({ ...errors, name: undefined });
                                }}
                                placeholder={t('applications.namePlaceholder')}
                                className={`create-application-input ${
                                    errors.name ? 'error' : ''
                                }`}
                            />
                            {errors.name && (
                                <span className="create-application-error">{t(errors.name)}</span>
                            )}
                        </div>

                        <div className="create-application-field">
                            <label className="create-application-label">
                                {t('applications.scope')}
                            </label>
                            <Autocomplete
                                ref={autocompleteRef}
                                multiple
                                options={scopes}
                                disableCloseOnSelect
                                open={scopeDropdownOpen}
                                onOpen={() => setScopeDropdownOpen(true)}
                                onClose={() => setScopeDropdownOpen(false)}
                                getOptionLabel={(option) => option.name}
                                value={scopes.filter(s => selectedScopes.includes(s.id))}
                                onChange={(e, newValue) => {
                                    setSelectedScopes(newValue.map(v => v.id));
                                    setErrors({ ...errors, scopes: undefined });
                                }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        placeholder={t('applications.scopePlaceholder')}
                                        error={!!errors.scopes}
                                    />
                                )}
                                ListboxComponent={(props) => {
                                    const allSelected = selectedScopes.length === scopes.length && scopes.length > 0;
                                    
                                    return (
                                        <ul {...props}>
                                            <li
                                                style={{
                                                    padding: '8px 16px',
                                                    cursor: 'pointer',
                                                    borderBottom: '1px solid #e0e0e0',
                                                    backgroundColor: '#f5f5f5',
                                                    fontWeight: 'bold',
                                                }}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    
                                                    if (allSelected) {
                                                        setSelectedScopes([]);
                                                    } else {
                                                        setSelectedScopes(scopes.map(s => s.id));
                                                    }
                                                    setErrors({ ...errors, scopes: undefined });
                                                    
                                                    // Close dropdown immediately
                                                    setScopeDropdownOpen(false);
                                                    
                                                    // Blur the input to fully close
                                                    if (document.activeElement instanceof HTMLElement) {
                                                        document.activeElement.blur();
                                                    }
                                                }}
                                            >
                                                {t('applications.selectAll')}
                                            </li>
                                            {props.children}
                                        </ul>
                                    );
                                }}
                                className="create-application-autocomplete"
                            />
                           
                            {errors.scopes && (
                                <span className="create-application-error">{t(errors.scopes)}</span>
                            )}
                        </div>

                        <div className="create-application-actions">
                            <button
                                type="submit"
                                className="create-application-btn primary"
                                disabled={isCreating}
                            >
                                {isCreating ? t('applications.generating') : t('applications.generateApplication')}
                            </button>
                        </div>
                    </form>
                </div>
            
        </div>
    );
};

export default CreateApplicationForm;
